import {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {KIND_MEMORY_READY, usePendingWork} from '../context/PendingWorkContext';
import './PendingWorkNotifier.scss';

// Aviso de "terminó de procesarse". No renderiza nada: observa el trabajo pendiente y, cuando algo
// que ESTE usuario subió termina, dispara un toast accionable — esté donde esté en la app. Ese es el
// caso que lo motiva: subir seis reportes, irse a navegar y nunca enterarse de que quedaron hechos
// esperando revisión.
//
// El toast NO se auto-cierra: es el acuse de recibo de un trabajo que el usuario disparó y que
// termina en una acción suya. Un aviso de 5 segundos que aparece mientras mira otra pantalla es
// exactamente el problema que vino a resolver.

// Marca de los documentos ya avisados, para que un remount, un F5 o el siguiente poll no repitan el
// mismo aviso. Con TTL: sin purga, la lista crecería para siempre en localStorage.
const NOTIFIED_KEY = 'dashbo_pending_work_notified';
const NOTIFIED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function readNotified() {
    try {
        const stored = JSON.parse(localStorage.getItem(NOTIFIED_KEY) || 'null');
        if (!stored || typeof stored !== 'object') return {};
        const cutoff = Date.now() - NOTIFIED_TTL_MS;
        const fresh = {};
        Object.keys(stored).forEach((id) => {
            if (typeof stored[id] === 'number' && stored[id] > cutoff) fresh[id] = stored[id];
        });
        return fresh;
    } catch {
        return {};
    }
}

function writeNotified(notified) {
    try {
        localStorage.setItem(NOTIFIED_KEY, JSON.stringify(notified));
    } catch { /* incógnito / storage lleno: a lo sumo el aviso se repite una vez */ }
}

export function PendingWorkNotifier() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {items, loaded, pendingFactsByClient} = usePendingWork();
    // Los conteos de cola se leen SIN ser dependencia del efecto: son el detalle del aviso, no su
    // disparador. Sin esto, cada refresco del contador re-evaluaría los avisos.
    const factsRef = useRef(pendingFactsByClient);
    factsRef.current = pendingFactsByClient;

    useEffect(() => {
        if (!loaded) return;
        const ready = items.filter((item) => item.kind === KIND_MEMORY_READY);
        if (ready.length === 0) return;

        const notified = readNotified();
        let changed = false;

        ready.forEach((item) => {
            const documentIds = (item.document_ids || []).map(String);
            const fresh = documentIds.filter((id) => !notified[id]);
            // Si TODOS los documentos del grupo ya se avisaron, no hay noticia. Alcanza con uno nuevo
            // para volver a avisar: el aviso habla del cliente, no del documento.
            if (fresh.length === 0) return;

            // Mostrar PRIMERO y marcar después. Al revés, un aviso que no llegó a pantalla dejaba
            // esos documentos marcados por 7 días — o sea, silenciados para siempre.
            showToast(item);
            documentIds.forEach((id) => { notified[id] = Date.now(); });
            changed = true;
        });

        if (changed) writeNotified(notified);

        function showToast(item) {
            const pendingFacts = factsRef.current[String(item.client_id)] || 0;
            const failed = item.failed_count || 0;
            const allFailed = failed === item.count;
            // Un fallo lleva a Fuentes (donde se reintenta), no a una cola que puede estar vacía.
            const destination = allFailed
                ? `/client/${item.client_id}/memory/sources`
                : `/client/${item.client_id}/memory/facts`;
            const toastId = `pending-work-${item.client_id}`;
            const type = allFailed ? 'error' : 'success';

            const content = (
                <div className="pw-toast">
                    <p className="pw-toast__title">
                        {allFailed
                            ? t('pending_work.toast.title_failed', {client: item.client_name,
                                interpolation: {escapeValue: false}})
                            : t('pending_work.toast.title', {client: item.client_name,
                                interpolation: {escapeValue: false}})}
                    </p>
                    <p className="pw-toast__line">
                        {t('pending_work.toast.documents', {count: item.count})}
                        {failed > 0 && !allFailed
                            && ` · ${t('pending_work.toast.some_failed', {count: failed})}`}
                    </p>
                    {!allFailed && pendingFacts > 0 && (
                        <p className="pw-toast__line -highlight">
                            {t('pending_work.toast.pending_facts', {count: pendingFacts})}
                        </p>
                    )}
                    <button type="button" className="pw-toast__cta"
                            onClick={() => {
                                toast.dismiss(toastId);
                                navigate(destination);
                            }}>
                        {allFailed ? t('pending_work.toast.cta_failed') : t('pending_work.toast.cta')}
                    </button>
                </div>
            );

            // 🔴 Con un toast de ese id ya en pantalla, `toast()` se DESCARTA EN SILENCIO
            // (react-toastify 11): no encola ni actualiza. Y como estos avisos no se auto-cierran,
            // ese es el caso normal, no el borde — subís seis documentos, terminan tres, y los otros
            // tres no avisan nunca. Por eso se actualiza el que ya está.
            //
            // Actualizar muestra información MEJOR, no parcial: el backend acumula todos los
            // documentos del cliente terminados en la ventana, así que el item nuevo trae los seis.
            if (toast.isActive(toastId)) {
                toast.update(toastId, {render: content, type});
                return;
            }
            toast(content, {toastId, autoClose: false, className: 'pw-toast-container', type});
        }
    }, [items, loaded, navigate, t]);

    return null;
}
